import React, { useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const Section = styled.section`
  --pricing-ink-color: #2d3647;

  width: 100%;
  background: linear-gradient(180deg, #ffffff 0%, rgba(221, 214, 254, 0.15) 47.97%, #ffffff 100%);
  padding: 0;
  position: relative;
  overflow: visible;
`;

const Container = styled.div<{ $compactTopSpacing?: boolean }>`
  position: relative;
  margin: ${props => props.$compactTopSpacing ? '0 auto' : '80px auto'};
  width: 100%;
  max-width: ${props => props.$compactTopSpacing ? 'var(--landing-content-max)' : '1400px'};
  padding: ${props => props.$compactTopSpacing ? 'var(--landing-section-top) var(--landing-page-gutter) var(--landing-section-bottom)' : '48px'};

  @media (max-width: 1024px) {
    padding: ${props => props.$compactTopSpacing ? 'var(--landing-section-top) var(--landing-page-gutter) var(--landing-section-bottom)' : '48px 24px'};
  }

  @media (max-width: 768px) {
    padding: ${props => props.$compactTopSpacing ? 'var(--landing-section-top) var(--landing-page-gutter) var(--landing-section-bottom)' : '24px'};
    margin: ${props => props.$compactTopSpacing ? '0 auto' : '40px auto'};
  }

  @media (max-width: 480px) {
    padding: ${props => props.$compactTopSpacing ? 'var(--landing-section-top) var(--landing-page-gutter) var(--landing-section-bottom)' : '16px 12px'};
    margin: ${props => props.$compactTopSpacing ? '0 auto' : '24px auto'};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  font-family: "Space Grotesk", sans-serif;
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #0f1116;
  margin: 0 0 0.5rem 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    margin: 0 0 0.45rem 0;
  }
`;

const Subtitle = styled.p`
  font-family: "Public Sans", sans-serif;
  font-size: 1rem;
  line-height: 1.55;
  font-weight: 400;
  color: #666a72;
  margin: 0;
  text-align: center;
  max-width: 72ch;
  margin-inline: auto;

  @media (max-width: 900px) {
    font-size: 0.98rem;
  }

  @media (max-width: 480px) {
    font-size: 0.92rem;
  }
`;

const TabWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const TabList = styled.div`
  display: flex;
  background: #f3f4f6;
  border-radius: 1000px;
  padding: 0.25rem;
  gap: 0.25rem;
`;

const Tab = styled.button<{ active: boolean }>`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  font-weight: 500;
  border: none;
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  color: ${props => props.active ? '#0b0f1a' : '#6b7280'};
  border-radius: 1000px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.active ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'};

  &:hover {
    color: #0b0f1a;
  }
`;

const SaveTag = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #7c3aed;
  background: #ede9fe;
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  margin-left: 0.5rem;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 380px));
  gap: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  justify-content: space-between;

  @media (max-width: 1240px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    gap: 16px;
    padding: 16px 0 24px;
    margin: 0;
    padding-left: 0;
    padding-right: 0;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ScrollHint = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #6b7280;
    font-size: 13px;
    margin-bottom: 12px;
    font-family: 'Space Grotesk', sans-serif;
    animation: pulseHint 2s infinite ease-in-out;
  }
  
  @keyframes pulseHint {
    0%, 100% { opacity: 0.6; transform: translateX(0); }
    50% { opacity: 1; transform: translateX(4px); }
  }
`;


const CarouselIndicators = styled.div`
  display: none;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const CarouselDot = styled.button<{ active: boolean }>`
  width: ${props => props.active ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${props => props.active ? '#a78bfa' : '#e5e7eb'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
`;

const PricingCard = styled.div<{ isHighlighted?: boolean }>`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 35px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }

  ${props => props.isHighlighted && `
    border: 2px solid transparent;
    background: linear-gradient(#fff, #fff) padding-box,
                linear-gradient(135deg, #9ce8dc 0%, #39b9aa 48%, #168a82 100%) border-box;
    box-shadow: 0 8px 24px rgba(22, 138, 130, 0.13);
  `}

  @media (max-width: 768px) {
    flex: 0 0 85%;
    min-width: 280px;
    max-width: 320px;
    scroll-snap-align: center;
  }
`;

const HighlightBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background:
    radial-gradient(circle at 8% 10%, rgba(45, 212, 191, 0.18), transparent 34%),
    linear-gradient(115deg, #123c3a 0%, #173f47 54%, #1f3448 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(15, 58, 60, 0.2);
  font-family: 'Segoe UI', sans-serif;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const PlanHeader = styled.div`
  margin-bottom: 0.75rem;
`;

const PlanName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: #0b0f1a;
  margin: 0 0 0.5rem 0;
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DiscountBadge = styled.span`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #7c3aed;
  background: #ede9fe;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
`;

const PriceSymbol = styled.span`
  font-family: 'Dirham', sans-serif;
  font-size: 0.75em;
  font-weight: 500;
  margin-right: 8px;
  line-height: 1;
`;

const PriceValue = styled.div`
  display: flex;
  align-items: center;
`;

const PriceSection = styled.div`
  margin-bottom: 1rem;
`;

const PriceAmount = styled.div`
  font-family: 'Public Sans', sans-serif;
  font-size: 3.25rem;
  font-weight: 800;
  line-height: 0.85;
  letter-spacing: -0.08em;
  color: var(--pricing-ink-color);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`;

const PriceLabel = styled.span`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: normal;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ReturnsInfo = styled.div`
  font-size: 14px;
  line-height: 1.5;
  font-weight: 400;
  color: #6b7280;
  text-align: left;
  margin-top: 0.5rem;
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 63px; /* 14px font-size * 1.5 line-height * 3 lines */
  min-height: 63px;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Button = styled.a`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 1.5rem;
  font-size: 16px;
  font-weight: 600;
  border-radius: var(--smush-cta-radius);
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
`;

const PrimaryButton = styled(Button)`
  background: var(--pricing-ink-color);
  color: #ffffff !important;
  border: none;

  &:hover {
    background: color-mix(in srgb, var(--pricing-ink-color) 88%, white);
  }
`;

// const SecondaryButton = styled(Button)`
//   background: #f3f4f6;
//   color: #111827;
//   border: 1px solid transparent;

//   &:hover {
//     background: #e5e7eb;
//   }
// `;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem 0;
  flex: 1;
`;

const FeatureItem = styled.li`
  font-size: 14px;
  line-height: 1.5;
  font-weight: 400;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  padding-left: 1.25rem;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.125rem;
    width: 0.75rem;
    height: 0.75rem;
    flex-shrink: 0;
    background-image: url("data:image/svg+xml,%3Csvg preserveAspectRatio='xMidYMid meet' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.0947 2.49902C11.3057 2.7334 11.3057 3.08496 11.0947 3.2959L4.90723 9.4834C4.67285 9.71777 4.32129 9.71777 4.11035 9.4834L0.922852 6.2959C0.688477 6.08496 0.688477 5.7334 0.922852 5.52246C1.13379 5.28809 1.48535 5.28809 1.69629 5.52246L4.48535 8.31152L10.2979 2.49902C10.5088 2.28809 10.8604 2.28809 11.0713 2.49902H11.0947Z' fill='%23a78bfa'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
  }
`;

const ViewAllButton = styled.button`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  background: none;
  border: none;
  color: #0b0f1a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0;
  text-decoration: underline;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    color: #a78bfa;
  }
`;

// const ExtraInfo = styled.div`
//   font-family: "Tt Firs Neue", Arial, sans-serif;
//   font-size: 0.75rem;
//   line-height: 1.417em;
//   font-weight: 400;
//   color: #6b7280;
//   text-align: center;
//   margin-top: 0.5rem;
// `;

// Modal styles
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1.25rem;
  animation: ${fadeIn} 0.2s ease-out;

  @media (max-width: 480px) {
    padding: 0.75rem;
    align-items: flex-end;
  }
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 1rem;
  max-width: 75rem;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  animation: ${slideUp} 0.3s ease-out;
  overflow: hidden;

  @media (max-width: 480px) {
    max-height: 95vh;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1.5rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1rem;
  }
`;

const ModalTitle = styled.h2`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1.75rem;
  font-weight: 500;
  line-height: 1.25em;
  letter-spacing: -0.07rem;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.75rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 0 2rem 2rem;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem 1.5rem;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(156, 163, 175, 0.5);
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(156, 163, 175, 0.7);
  }
`;

const ComparisonTable = styled.div<{ planCount: number }>`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr repeat(${props => props.planCount}, 1fr);
  gap: 0;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  overflow: hidden;

  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    
    &::-webkit-scrollbar {
      height: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
    }
  }
`;

const TableHeaderRow = styled.div`
  display: contents;
`;

const TableFeatureRow = styled.div`
  display: contents;

  &:hover > div {
    background: #f9fafb;
  }
`;

const TableHeaderCell = styled.div`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  background: #f9fafb;
  padding: 1.5rem 1rem;
  text-align: center;
  border-bottom: 2px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;

  &:first-of-type {
    border-left: none;
    text-align: left;
    align-items: flex-start;
  }

  &:last-of-type {
    border-right: none;
  }
`;

const PlanHeaderName = styled.h3`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: #0b0f1a;
  margin: 0;
`;

const PlanHeaderPrice = styled.div`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #6b7280;
  margin: 0;
`;

const PlanHeaderButton = styled.button`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  background: var(--smush-cta-primary);
  color: #ffffff;
  border: none;
  border-radius: var(--smush-cta-radius);
  padding: 0.75rem 1.5rem;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: var(--smush-cta-primary-hover);
  }
`;

const TableCell = styled.div`
  font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  padding: 1rem;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  font-size: 14px;

  &:first-of-type {
    justify-content: flex-start;
    font-weight: 500;
    color: #0b0f1a;
    border-left: none;
  }

  &:last-of-type {
    border-right: none;
  }
`;

const CheckIcon = styled.span`
  color: #a78bfa;
  font-weight: 600;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    width: 0.75rem;
    height: 0.75rem;
    background-image: url("data:image/svg+xml,%3Csvg preserveAspectRatio='xMidYMid meet' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.0947 2.49902C11.3057 2.7334 11.3057 3.08496 11.0947 3.2959L4.90723 9.4834C4.67285 9.71777 4.32129 9.71777 4.11035 9.4834L0.922852 6.2959C0.688477 6.08496 0.688477 5.7334 0.922852 5.52246C1.13379 5.28809 1.48535 5.28809 1.69629 5.52246L4.48535 8.31152L10.2979 2.49902C10.5088 2.28809 10.8604 2.28809 11.0713 2.49902H11.0947Z' fill='%23a78bfa'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
    display: inline-block;
  }
`;

const CategoryHeader = styled.div`
  grid-column: 1 / -1;
  background: #f9fafb;
  padding: 1rem;
  font-weight: 600;
  color: #111827;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e5e7eb;
  border-top: 1px solid #e5e7eb;
  font-family: 'Segoe UI', sans-serif;

  &:first-of-type {
    border-top: none;
  }
`;

interface Plan {
  id: string;
  name: string;
  priceYearly: string;
  priceMonthly: string;
  description: string;
  features: string[];
  showDiscount?: boolean;
  highlightText?: string;
}

const plans: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    priceYearly: '$19',
    priceMonthly: '$24',
    description: 'For hobbyists, bloggers, and creators who want to instantly answer visitor questions and turn casual interest into loyal followers.',
    features: [
      '1 Chatbot',
      '1 Lead Generation Form',
      '1000 monthly Smart Responses',
      '20 File Uploads',
      'Train up to 30 URLs',
      'Basic Analytics',
      '3 FAQ Menu Items',
      '3 Month Conversation History',
      'Standard AI Model',
      'Training & Setup Done for You (optional)'
    ],
    showDiscount: true
  },
  {
    id: 'pro',
    name: 'Pro',
    priceYearly: '$30',
    priceMonthly: '$38',
    description: 'For growing startups and small businesses ready to scale support without hiring large teams. Automate engagement, capture leads, and stay personal at scale.',
    features: [
      '3 Chatbots',
      '10 Lead Generation Forms',
      '50,000 monthly Smart Responses',
      '100 File Uploads',
      'Train up to 1000 URLs',
      'Advanced Analytics',
      'Unlimited FAQ Menu Items',
      '1 Year Conversation History',
      'Premium AI Model',
      'Optionally Bring your own LLM',
      'White Labeling',
      'External Integrations',
      '30 Intent Based workflows',
      'Training & Setup Done for You (optional)'
    ],
    showDiscount: true,
    highlightText: 'ENTERPRISE FEATURES, STARTUP PRICE'
  },
  {
    id: 'unlimited',
    name: 'Unlimited',
    priceYearly: '$100',
    priceMonthly: '$125',
    description: 'For high-volume brands managing multiple domains and teams. Unlock full automation, human handoff, and detailed analytics for total customer control.',
    features: [
      'Unlimited Chatbots',
      'Unlimited Lead Generation Forms',
      '10x more monthly Smart Responses than Pro',
      '500 File Uploads',
      'Train up to 5000 URLs',
      'Pro Analytics',
      'Unlimited FAQ Menu Items',
      '3 Year Conversation History',
      'Human handoff',
      'Premium AI Model',
      'Optionally Bring your own LLM',
      'White Labeling',
      'External Integrations',
      'Unlimited Intent Based workflows',
      'Training & Setup Done for You (optional)'
    ],
    showDiscount: true
  }
];

const comparisonCategories = [
  {
    title: 'Usage Limits',
    features: [
      { name: 'Chatbots', values: { Starter: '1', Pro: '3', Unlimited: 'Unlimited' } },
      { name: 'Lead Gen Forms', values: { Starter: '1', Pro: '10', Unlimited: 'Unlimited' } },
      { name: 'Smart Responses', values: { Starter: '1,000/mo', Pro: '50,000/mo', Unlimited: '500,000/mo' } },
      { name: 'File Uploads', values: { Starter: '20 files', Pro: '100 files', Unlimited: '500 files' } },
      { name: 'URL Training', values: { Starter: '30 URLs', Pro: '1,000 URLs', Unlimited: '5,000 URLs' } },
    ]
  },
  {
    title: 'Platform Features',
    features: [
      { name: 'Analytics', values: { Starter: 'Basic', Pro: 'Advanced', Unlimited: 'Pro' } },
      { name: 'FAQ Menu Items', values: { Starter: '3', Pro: 'Unlimited', Unlimited: 'Unlimited' } },
      { name: 'Conversation History', values: { Starter: '3 Months', Pro: '1 Year', Unlimited: '3 Years' } },
      { name: 'Intent Workflows', values: { Starter: false, Pro: '30', Unlimited: 'Unlimited' } },
      { name: 'Human Handoff', values: { Starter: false, Pro: false, Unlimited: true } },
    ]
  },
  {
    title: 'Advanced Capabilities',
    features: [
      { name: 'AI Model', values: { Starter: 'Standard', Pro: 'Premium', Unlimited: 'Premium' } },
      { name: 'Bring Your Own LLM', values: { Starter: false, Pro: true, Unlimited: true } },
      { name: 'White Labeling', values: { Starter: false, Pro: true, Unlimited: true } },
      { name: 'External Integrations', values: { Starter: false, Pro: true, Unlimited: true } },
      { name: 'Training & Setup', values: { Starter: 'Optional', Pro: 'Optional', Unlimited: 'Optional' } },
    ]
  }
];

interface PricingCardsProps {
  compactTopSpacing?: boolean;
}

const PricingCards: React.FC<PricingCardsProps> = ({ compactTopSpacing = false }) => {
  const [billingPeriod, setBillingPeriod] = useState<'yearly' | 'monthly'>('monthly');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState(1); // Start with Pro (middle card)
  const gridRef = React.useRef<HTMLDivElement>(null);

  // Regional pricing state
  const [pricing, setPricing] = React.useState({
    symbol: '$',
    starter: { monthly: 24, yearly: 19 },
    pro: { monthly: 38, yearly: 30 },
    unlimited: { monthly: 125, yearly: 100 },
    disclaimer: null as string | null
  });

  // Fetch regional pricing on mount
  React.useEffect(() => {
    const fetchPricing = async () => {
      try {
        // Forward any ?region= param from URL for local testing
        const urlParams = new URLSearchParams(window.location.search);
        const regionParam = urlParams.get('region');
        const apiUrl = regionParam ? `/api/pricing?region=${regionParam}` : '/api/pricing';

        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          setPricing({
            symbol: data.symbol,
            starter: data.plans.starter,
            pro: data.plans.pro,
            unlimited: data.plans.unlimited,
            disclaimer: data.disclaimer
          });
        }
      } catch (error) {
        console.log('Using default USD pricing');
      }
    };
    fetchPricing();
  }, []);

  // Get dynamic price for a plan
  const getPrice = (planId: string) => {
    const isYearly = billingPeriod === 'yearly';

    switch (planId) {
      case 'starter':
        return isYearly ? Math.round(pricing.starter.yearly / 12) : pricing.starter.monthly;
      case 'pro':
        return isYearly ? Math.round(pricing.pro.yearly / 12) : pricing.pro.monthly;
      case 'unlimited':
        return isYearly ? Math.round(pricing.unlimited.yearly / 12) : pricing.unlimited.monthly;
      default:
        return '0';
    }
  };

  // Calculate card width dynamically based on screen size
  const getCardWidth = () => {
    if (!gridRef.current) return 300;
    const firstCard = gridRef.current.querySelector('div') as HTMLElement;
    if (firstCard) {
      return firstCard.offsetWidth + 16; // card width + gap
    }
    return 300;
  };

  // Handle scroll to update active indicator
  const handleScroll = () => {
    if (gridRef.current) {
      const scrollLeft = gridRef.current.scrollLeft;
      const cardWidth = getCardWidth();
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveCardIndex(Math.max(0, Math.min(newIndex, plans.length - 1)));
    }
  };

  // Scroll to card when indicator is clicked
  const scrollToCard = (index: number) => {
    if (gridRef.current) {
      const cardWidth = getCardWidth();
      gridRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setActiveCardIndex(index);
    }
  };

  // Scroll to center card (Pro) on mobile initial load
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (gridRef.current && window.innerWidth <= 768) {
        const cardWidth = getCardWidth();
        gridRef.current.scrollTo({
          left: 1 * cardWidth,
          behavior: 'auto'
        });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Section id="pricing">
        <Container $compactTopSpacing={compactTopSpacing}>
          <Header>
            <Title>Plans that grow with your business</Title>
            <Subtitle>Choose the usage limits that fit your business. Setup and onboarding are included with every paid plan.</Subtitle>
          </Header>

          <TabWrapper>
            <TabList>
              <Tab
                active={billingPeriod === 'monthly'}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </Tab>
              <Tab
                active={billingPeriod === 'yearly'}
                onClick={() => setBillingPeriod('yearly')}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                Yearly
                <SaveTag>Save 20%</SaveTag>
              </Tab>
            </TabList>
          </TabWrapper>

          <ScrollHint>
            <span>Swipe to explore plans</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </ScrollHint>

          <PricingGrid ref={gridRef} onScroll={handleScroll}>
            {plans.map((plan, index) => {
              const price = getPrice(plan.id);
              const displayedFeatures = plan.features.slice(0, 6);

              return (
                <PricingCard key={index} isHighlighted={!!plan.highlightText}>
                  {plan.highlightText && <HighlightBadge>{plan.highlightText}</HighlightBadge>}
                  <PlanHeader>
                    <PlanName>
                      {plan.name}
                      {plan.showDiscount && billingPeriod === 'yearly' && (
                        <DiscountBadge>20% off</DiscountBadge>
                      )}
                    </PlanName>
                  </PlanHeader>

                  <PriceSection>
                    <PriceAmount>
                      <PriceValue><PriceSymbol>{pricing.symbol}</PriceSymbol>{price}</PriceValue>
                      <PriceLabel>per month</PriceLabel>
                    </PriceAmount>
                    <ReturnsInfo>{plan.description}</ReturnsInfo>
                  </PriceSection>

                  <ActionButtons>
                    <PrimaryButton href="#" onClick={(e) => {
                      e.preventDefault();
                      const planId = `${plan.id}_${billingPeriod}`;
                      const planData = {
                        ...plan,
                        id: planId,
                        billingPeriod,
                        planType: 'subscription'
                      };
                      localStorage.setItem('selectedPlan', JSON.stringify(planData));
                      window.location.href = '/get-started';
                    }}>
                      Get Started
                    </PrimaryButton>
                  </ActionButtons>

                  <FeaturesList>
                    {displayedFeatures.map((feature, featureIndex) => (
                      <FeatureItem key={featureIndex}>{feature}</FeatureItem>
                    ))}
                  </FeaturesList>

                  <ViewAllButton onClick={() => setIsModalOpen(true)}>
                    View all features →
                  </ViewAllButton>
                </PricingCard>
              );
            })}
          </PricingGrid>

          <CarouselIndicators>
            {plans.map((_, index) => (
              <CarouselDot
                key={index}
                active={activeCardIndex === index}
                onClick={() => scrollToCard(index)}
              />
            ))}
          </CarouselIndicators>
        </Container>
      </Section>

      {isModalOpen && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Compare our plans</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>

            <ModalBody>
              <ComparisonTable planCount={plans.length}>
                {/* Header Row with Plan Names, Prices, and CTAs */}
                <TableHeaderRow>
                  <TableHeaderCell>
                    <span style={{ fontWeight: 500, fontSize: '0.875rem', color: '#111827' }}>Feature</span>
                  </TableHeaderCell>
                  {plans.map((plan, index) => {
                    const price = billingPeriod === 'yearly' ? plan.priceYearly : plan.priceMonthly;
                    return (
                      <TableHeaderCell key={index}>
                        <PlanHeaderName>{plan.name}</PlanHeaderName>
                        <PlanHeaderPrice>{price} per month</PlanHeaderPrice>
                        <PlanHeaderButton onClick={() => {
                          const planData = { ...plan, billingPeriod, price };
                          localStorage.setItem('selectedPlan', JSON.stringify(planData));
                          window.location.href = '/get-started';
                        }}>
                          Choose plan
                        </PlanHeaderButton>
                      </TableHeaderCell>
                    );
                  })}
                </TableHeaderRow>

                {/* Feature Rows */}
                {/* Feature Rows */}
                {comparisonCategories.map((category, catIdx) => (
                  <React.Fragment key={catIdx}>
                    <CategoryHeader>{category.title}</CategoryHeader>
                    {category.features.map((feature, featIdx) => (
                      <TableFeatureRow key={featIdx}>
                        <TableCell>{feature.name}</TableCell>
                        {plans.map((plan, planIdx) => {
                          const value = feature.values[plan.name as keyof typeof feature.values];
                          return (
                            <TableCell key={planIdx}>
                              {typeof value === 'boolean' ? (
                                value ? <CheckIcon /> : <span style={{ color: '#d1d5db' }}>—</span>
                              ) : (
                                value
                              )}
                            </TableCell>
                          );
                        })}
                      </TableFeatureRow>
                    ))}
                  </React.Fragment>
                ))}
              </ComparisonTable>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default PricingCards;
